/**
 * What Evidence chat can do in this deployment (MOD-1726, guardrail F9).
 *
 * An on-prem appliance is air-gapped or holds named outbound grants, and one
 * image serves every posture, so "can Evidence search the web here?" is asked
 * of the backend at runtime. The frontend learns named capabilities only —
 * never the egress mode or the grants behind them.
 */

/** Wire shape of `GET /chat/capabilities`: a map keyed by capability name. */
export interface ChatCapabilitiesResponse {
  capabilities: Record<string, { available: boolean }>;
}

/** The capabilities this build knows how to act on. */
export interface EvidenceCapabilities {
  readonly webSearch: boolean;
}

/** A deployment whose capabilities are fixed at build time: everything is available. */
export const EVIDENCE_CAPABILITIES_UNRESTRICTED: EvidenceCapabilities = Object.freeze({
  webSearch: true,
});

/** The answer while loading, on error and for a body that does not parse. */
export const EVIDENCE_CAPABILITIES_MOST_RESTRICTIVE: EvidenceCapabilities = Object.freeze(
  {
    webSearch: false,
  }
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Only a literal `available: true` under the name counts; a missing name is not available. */
const isAvailable = (capabilities: Record<string, unknown>, name: string): boolean => {
  const entry = capabilities[name];
  return isRecord(entry) && entry.available === true;
};

/**
 * Resolves what the UI and the request builders may rely on.
 *
 * - `reportedByBackend: false` → unrestricted, whatever `response` holds. The
 *   app decides this at build time and makes no request.
 * - Otherwise the answer is read from `response`, and anything short of a
 *   well-formed body (not loaded yet, failed, route missing, malformed) is the
 *   most restrictive value. Never optimistic.
 *
 * Unknown capability names are ignored, so a newer backend cannot widen what
 * this build does. The two all-or-nothing answers are shared constants, so a
 * subscriber does not re-render for an equal value.
 */
export function resolveEvidenceCapabilities({
  reportedByBackend,
  response,
}: {
  reportedByBackend: boolean;
  response: unknown;
}): EvidenceCapabilities {
  if (!reportedByBackend) return EVIDENCE_CAPABILITIES_UNRESTRICTED;
  if (!isRecord(response) || !isRecord(response.capabilities)) {
    return EVIDENCE_CAPABILITIES_MOST_RESTRICTIVE;
  }
  return isAvailable(response.capabilities, 'web_search')
    ? EVIDENCE_CAPABILITIES_UNRESTRICTED
    : EVIDENCE_CAPABILITIES_MOST_RESTRICTIVE;
}
